import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { ClipboardList, Play } from "lucide-react";
import { toast } from "sonner";
import WorkoutLogger from "@/components/workouts/WorkoutLogger";
import WorkoutHistory from "@/components/workouts/WorkoutHistory";
import WorkoutProgression from "@/components/workouts/WorkoutProgression";
import LiveWorkout, { readLiveSession, writeLiveSession, clearLiveSession, deleteServerDraft } from "@/components/workouts/LiveWorkout";
import VideoPlayerModal from "@/components/videos/VideoPlayerModal";
import { useUnits } from "@/lib/useUnits";
import { kgToLb } from "@/lib/units";
import { queueWorkout, flushWorkouts, getQueuedCount, subscribeQueue } from "@/lib/offlineQueue";

const empty = () => ({
  name: "",
  rest_seconds: 90,
  sets: [{ reps: 10, weight_kg: 0, rpe: 7 }],
  notes: "",
});

export default function Workouts() {
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(45);
  const [pe, setPe] = useState(7);
  const [notes, setNotes] = useState("");
  const [exercises, setExercises] = useState([empty()]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [progression, setProgression] = useState(null);
  const [loadingProg, setLoadingProg] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [exerciseDb, setExerciseDb] = useState([]);
  const [activeSuggest, setActiveSuggest] = useState(null);
  const loggerRef = useRef(null);
  const { isImperial, setUnits } = useUnits();
  const [pendingSync, setPendingSync] = useState(getQueuedCount());
  // Live "workout in progress" mode. Auto-resume an interrupted session.
  const [live, setLive] = useState(() => Boolean(readLiveSession()));
  // Coach cue videos linked to exercise names (library feature).
  const [exerciseVideos, setExerciseVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);

  const videoForExercise = (name) => {
    const n = (name || "").trim().toLowerCase();
    if (!n) return null;
    return exerciseVideos.find((v) => (v.exercise_name || "").trim().toLowerCase() === n) || null;
  };

  useEffect(() => {
    const unsub = subscribeQueue(setPendingSync);
    // If workouts were logged offline last session, push them up now.
    flushWorkouts().then((n) => n && load());
    // No local live session? Check the server for a draft saved when a tab
    // was closed mid-workout (possibly on another device) and resume it.
    if (!readLiveSession()) {
      api
        .get("/workouts/draft")
        .then((r) => {
          const s = r.data?.session;
          if (s && Array.isArray(s.exercises)) {
            writeLiveSession(s);
            setLive(true);
            toast.success("Resumed your in-progress workout");
          }
        })
        .catch(() => {});
    }
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const [h, s, t, db, vids] = await Promise.all([
      api.get("/workouts"),
      api.get("/workouts/stats"),
      api.get("/templates/mine").catch(() => ({ data: [] })),
      api.get("/exercises").catch(() => ({ data: [] })),
      api.get("/videos").catch(() => ({ data: { videos: [] } })),
    ]);
    setHistory(h.data);
    setStats(s.data);
    setTemplates((t.data || []).filter((x) => x.kind === "workout"));
    setExerciseDb(db.data || []);
    setExerciseVideos((vids.data.videos || []).filter((v) => v.exercise_name));
  };
  useEffect(() => {
    load();
  }, []);

  // Fuzzy search for exercise suggestions while user types.
  const suggestionsFor = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return exerciseDb
      .filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.muscles.some((m) => m.toLowerCase().includes(q))
      )
      .slice(0, 6);
  };

  // Map exercise name -> full DB entry (for history thumbnails + deep-link).
  const exerciseFor = (name) => {
    if (!name) return null;
    const lc = name.toLowerCase().trim();
    return (
      exerciseDb.find((e) => e.name.toLowerCase() === lc) ||
      exerciseDb.find((e) => lc.includes(e.name.toLowerCase())) ||
      exerciseDb.find((e) => e.name.toLowerCase().includes(lc)) ||
      null
    );
  };

  // Apply a coach-assigned template (accepts both rich + concise shapes).
  const applyTemplate = (tpl) => {
    const data = tpl.data || {};
    const raw = data.exercises || data.workout?.exercises || [];
    if (!Array.isArray(raw) || raw.length === 0) {
      toast.error("Template has no exercises");
      return;
    }
    const normalized = raw.map((ex) => {
      let setsArr;
      if (Array.isArray(ex.sets)) {
        setsArr = ex.sets.map((s) => ({
          reps: Number(s.reps ?? ex.reps ?? 10),
          weight_kg: Number(s.weight_kg ?? s.weight ?? ex.weight_kg ?? 0),
          rpe: s.rpe != null ? Number(s.rpe) : ex.rpe != null ? Number(ex.rpe) : 7,
        }));
      } else {
        const count = Math.max(1, Number(ex.sets ?? 3));
        setsArr = Array.from({ length: count }, () => ({
          reps: Number(ex.reps ?? 10),
          weight_kg: Number(ex.weight_kg ?? ex.weight ?? 0),
          rpe: ex.rpe != null ? Number(ex.rpe) : 7,
        }));
      }
      return {
        name: String(ex.name || ex.exercise || ""),
        rest_seconds: Number(ex.rest_seconds ?? ex.rest ?? 90),
        sets: setsArr.length ? setsArr : [{ reps: 10, weight_kg: 0, rpe: 7 }],
        notes: ex.notes || "",
      };
    });
    setTitle(tpl.name || data.title || "Assigned workout");
    setDuration(Number(data.duration_min ?? duration));
    setNotes(data.notes || "");
    setExercises(normalized);
    toast.success(`Loaded "${tpl.name}" — review and save`);
    setTimeout(() => loggerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Quick-log: clone the most recent workout into the logger so the user can
  // adjust weights and save in 2 taps. Title is suffixed with "(repeat)" so
  // history stays readable.
  const repeatLast = () => {
    const last = history[0];
    if (!last) {
      toast.error("No previous workout to repeat");
      return;
    }
    setTitle(`${last.title || "Workout"} (repeat)`);
    setDuration(Number(last.duration_min ?? duration));
    setPe(Number(last.perceived_exertion ?? pe));
    setNotes("");
    setExercises(
      (last.exercises || []).map((ex) => ({
        name: ex.name || "",
        rest_seconds: Number(ex.rest_seconds ?? 90),
        notes: ex.notes || "",
        sets: (ex.sets || []).map((s) => ({
          reps: Number(s.reps ?? 10),
          weight_kg: Number(s.weight_kg ?? 0),
          rpe: s.rpe != null ? Number(s.rpe) : 7,
        })),
      })),
    );
    toast.success(`Pre-filled with "${last.title}" — adjust and save`);
    setTimeout(() => loggerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Exercise / set mutators
  const addEx = () => setExercises((arr) => [...arr, empty()]);
  const removeEx = (i) => setExercises((arr) => arr.filter((_, idx) => idx !== i));
  const updateEx = (i, patch) =>
    setExercises((arr) => arr.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const addSet = (i) =>
    setExercises((arr) =>
      arr.map((e, idx) =>
        idx === i ? { ...e, sets: [...e.sets, { reps: 10, weight_kg: 0, rpe: 7 }] } : e
      )
    );
  const updateSet = (i, j, patch) =>
    setExercises((arr) =>
      arr.map((e, idx) =>
        idx === i
          ? { ...e, sets: e.sets.map((s, jj) => (jj === j ? { ...s, ...patch } : s)) }
          : e
      )
    );
  const removeSet = (i, j) =>
    setExercises((arr) =>
      arr.map((e, idx) =>
        idx === i ? { ...e, sets: e.sets.filter((_, jj) => jj !== j) } : e
      )
    );

  // Shared submit path: POST with offline-queue fallback. Used by both the
  // classic form save and the live-session finish.
  const submitWorkout = async (payload, { onDone } = {}) => {
    try {
      await api.post("/workouts", payload);
      toast.success("Workout logged");
      onDone?.();
      load();
    } catch (e) {
      if (!e?.response) {
        // Network failure — save locally and sync when back online.
        queueWorkout(payload);
        toast.success("You're offline — workout saved on this device. It'll sync automatically.");
        onDone?.();
      } else {
        toast.error(e?.response?.data?.detail || "Failed to save workout");
      }
    }
  };

  const save = async () => {
    const payload = {
      title: title || `Workout ${new Date().toLocaleDateString()}`,
      duration_min: Number(duration),
      perceived_exertion: Number(pe),
      notes,
      exercises: exercises.filter((e) => e.name).map((e) => ({
        name: e.name,
        rest_seconds: Number(e.rest_seconds) || null,
        notes: e.notes || null,
        sets: e.sets.map((s) => ({
          reps: Number(s.reps),
          weight_kg: Number(s.weight_kg),
          rpe: s.rpe ? Number(s.rpe) : null,
        })),
      })),
    };
    await submitWorkout(payload, {
      onDone: () => {
        setTitle("");
        setNotes("");
        setExercises([empty()]);
      },
    });
  };

  const getProgression = async () => {
    setLoadingProg(true);
    try {
      const r = await api.get("/progression");
      setProgression(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Progression failed");
    } finally {
      setLoadingProg(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="overline text-blaze">/ Workouts</div>
        <h1 className="font-heading text-5xl uppercase mt-1">Log the work.</h1>
      </div>

      {pendingSync > 0 && (
        <div
          data-testid="offline-pending-banner"
          className="flex items-center justify-between gap-3 border border-yellow-500/40 bg-yellow-500/10 rounded-sm px-4 py-3"
        >
          <p className="text-sm text-yellow-200">
            {pendingSync} workout{pendingSync > 1 ? "s" : ""} saved offline — will sync when you're
            back online.
          </p>
          <button
            type="button"
            data-testid="offline-sync-now-btn"
            onClick={() => flushWorkouts().then((n) => n && load())}
            className="text-[10px] uppercase tracking-widest text-yellow-300 hover:underline whitespace-nowrap"
          >
            Sync now
          </button>
        </div>
      )}

      {/* Coach-assigned templates */}
      {templates.length > 0 && (
        <div className="obs-card" data-testid="assigned-templates">
          <div className="flex items-center gap-3 mb-3">
            <ClipboardList className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">From your coach</div>
              <h3 className="font-heading text-2xl uppercase">Assigned workouts</h3>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => {
              const exCount = (t.data?.exercises || t.data?.workout?.exercises || []).length;
              return (
                <div
                  key={`${t.id}-${t.assigned_at}`}
                  className="border border-obsidian-600 rounded-sm p-4 bg-obsidian-900/40 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-xl uppercase truncate">{t.name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                      {exCount} exercise{exCount === 1 ? "" : "s"} · assigned {new Date(t.assigned_at).toLocaleDateString()}
                    </div>
                    {t.data?.notes && (
                      <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{t.data.notes}</div>
                    )}
                  </div>
                  <button
                    data-testid={`apply-template-${t.id}`}
                    onClick={() => applyTemplate(t)}
                    className="blaze-btn px-4 py-2 text-xs inline-flex items-center gap-2 shrink-0"
                  >
                    <Play className="h-3 w-3" /> Apply
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {live ? (
        <LiveWorkout
          initial={{ title, exercises }}
          isImperial={isImperial}
          onFinish={async (payload) => {
            setLive(false);
            await submitWorkout(payload, {
              onDone: () => {
                setTitle("");
                setNotes("");
                setExercises([empty()]);
              },
            });
          }}
          onCancel={() => setLive(false)}
          history={history}
          videoForExercise={videoForExercise}
          onPlayVideo={setPlayingVideo}
        />
      ) : (
        <>
          <button
            type="button"
            data-testid="start-live-workout-btn"
            onClick={() => {
              // Defensive: discard any stale abandoned session so the fresh
              // plan below always imports cleanly.
              clearLiveSession();
              deleteServerDraft();
              setLive(true);
            }}
            className="w-full blaze-btn py-4 text-sm inline-flex items-center justify-center gap-2"
          >
            <Play className="h-4 w-4" /> Start live workout
            {exercises.some((e) => e.name) ? " — with the plan below" : ""}
          </button>

          <WorkoutLogger
            title={title}
            setTitle={setTitle}
            duration={duration}
            setDuration={setDuration}
            pe={pe}
            setPe={setPe}
            notes={notes}
            setNotes={setNotes}
            exercises={exercises}
            setExercises={setExercises}
            loggerRef={loggerRef}
            exerciseDb={exerciseDb}
            activeSuggest={activeSuggest}
            setActiveSuggest={setActiveSuggest}
            suggestionsFor={suggestionsFor}
            addEx={addEx}
            removeEx={removeEx}
            updateEx={updateEx}
            addSet={addSet}
            updateSet={updateSet}
            removeSet={removeSet}
            onSave={save}
            lastWorkout={history[0]}
            onRepeatLast={repeatLast}
            isImperial={isImperial}
            onToggleUnits={setUnits}
            videoForExercise={videoForExercise}
            onPlayVideo={setPlayingVideo}
          />
        </>
      )}

      <WorkoutProgression
        progression={progression}
        loading={loadingProg}
        onGenerate={getProgression}
      />

      <WorkoutHistory history={history} exerciseFor={exerciseFor} isImperial={isImperial} />

      {stats?.personal_records && Object.keys(stats.personal_records).length > 0 && (
        <div className="obs-card" data-testid="workout-prs">
          <div className="overline mb-2">Personal Records</div>
          <h3 className="font-heading text-3xl uppercase mb-4">PRs</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(stats.personal_records).map(([ex, kg]) => {
              const display = isImperial
                ? (Math.round(kgToLb(Number(kg)) * 2) / 2).toFixed(1)
                : Number(kg).toFixed(1);
              return (
                <div key={ex} className="stat-tile">
                  <div className="overline truncate">{ex}</div>
                  <div className="font-data text-2xl">
                    {display} <span className="text-xs text-zinc-500">{isImperial ? "lbs" : "kg"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {playingVideo && <VideoPlayerModal video={playingVideo} onClose={() => setPlayingVideo(null)} />}
    </div>
  );
}
